import React, { useEffect, useState } from 'react';
import { jsPDF } from 'jspdf';
import 'jspdf-autotable';
import { AuthenticatedLayout } from '../layouts/AuthenticatedLayout';
import { Card } from '../components/common/Card';
import { Button } from '../components/common/Button';
import { Pagination } from '../components/common/Pagination';
import { Download, TrendingUp, TrendingDown, DollarSign, Calendar, CheckCircle, AlertCircle, X } from 'lucide-react';
import { useTheme } from '../context/ThemeContext';
import { getTransactionReport } from '../services/analytics';

// Sample data for demo/fallback
const SAMPLE_DATA = {
  company_id: 'adb4734e9369402',
  company_name: 'FarmGo Harvest Hub',
  income: 2500000,
  expenses: 1250000,
  profit_loss: 1250000,
  transaction_count: 4,
  transactions: [
    { id: 1, date: new Date().toISOString(), description: 'Client Invoice Payment', category: 'Large Sales', type: 'income', amount: '500000.00' },
    { id: 2, date: new Date(Date.now() - 86400000).toISOString(), description: 'Office Rent', category: 'Rent', type: 'expense', amount: '50000.00' },
    { id: 3, date: new Date(Date.now() - 172800000).toISOString(), description: 'Marketing Campaign', category: 'Marketing', type: 'expense', amount: '150000.00' },
    { id: 4, date: new Date(Date.now() - 259200000).toISOString(), description: 'Equipment Purchase', category: 'Equipment', type: 'expense', amount: '200000.00' },
  ]
};

export const AnalyticsReportsPage = () => {
  const { darkMode } = useTheme();
  const [reportData, setReportData] = useState(SAMPLE_DATA);
  const [loading, setLoading] = useState(false);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [transactionPage, setTransactionPage] = useState(1);
  const [notification, setNotification] = useState(null);
  const ITEMS_PER_PAGE = 10;

  // Show toast notification
  const showNotification = (message, type = 'success') => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 3000);
  };

  // Convert date string (YYYY-MM-DD) to ISO timestamp
  const dateToISO = (dateString, isEndDate = false) => {
    const date = new Date(dateString);
    if (isEndDate) {
      // Set to end of day: 23:59:59.999Z
      date.setHours(23, 59, 59, 999);
    } else {
      // Set to start of day: 00:00:00.000Z
      date.setHours(0, 0, 0, 0);
    }
    return date.toISOString();
  };

  // Set default date range to last 30 days
  useEffect(() => {
    const today = new Date();
    const thirtyDaysAgo = new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000);
    
    setEndDate(today.toISOString().split('T')[0]);
    setStartDate(thirtyDaysAgo.toISOString().split('T')[0]);
  }, []);

  useEffect(() => {
    const fetchReportData = async () => {
      setLoading(true);
      try {
        const userJson = localStorage.getItem('user');
        const user = userJson ? JSON.parse(userJson) : null;
        
        // Try to get company_id from user object or localStorage fallback
        const companyId = user?.company_id || localStorage.getItem('company_id');

        if (!companyId) {
          showNotification('⚠️ Company ID not found. Please log in again.', 'error');
          setReportData(SAMPLE_DATA);
          setLoading(false);
          return;
        }

        if (companyId && startDate && endDate) {
          // Convert dates to ISO format for API
          const isoStartDate = dateToISO(startDate, false);
          const isoEndDate = dateToISO(endDate, true);
          
          // Fetch report data using the unified endpoint
          const data = await getTransactionReport(companyId, isoStartDate, isoEndDate);
          
          if (!data) {
            showNotification('❌ No data received from API', 'error');
            setReportData(SAMPLE_DATA);
            setLoading(false);
            return;
          }
          
          // Transform API response: convert string numbers to actual numbers
          const transformedData = {
            ...data,
            income: Math.abs(parseFloat(data?.income || 0)),
            expenses: Math.abs(parseFloat(data?.expenses || 0)),
            profit_loss: parseFloat(data?.profit_loss || 0),
            transaction_count: data?.transactions?.length || 0,
            transactions: data?.transactions ? data.transactions.map(t => ({
              ...t,
              amount: parseFloat(t.amount) || 0
            })) : []
          };
          
          setReportData(transformedData);
          showNotification('✅ Report loaded successfully', 'success');
        } else {
          setReportData(SAMPLE_DATA);
        }
      } catch (error) {
        showNotification(`❌ Failed to load report: ${error.message || 'Unknown error'}`, 'error');
        setReportData(SAMPLE_DATA);
      } finally {
        setLoading(false);
      }
    };

    if (startDate && endDate) {
      fetchReportData();
    }
  }, [startDate, endDate]);

  const handleExport = async (format) => {
    try {
      if (format === 'pdf') {
        // Create PDF document
        const doc = new jsPDF();
        const pageHeight = doc.internal.pageSize.getHeight();
        const pageWidth = doc.internal.pageSize.getWidth();
        const margin = 20;
        let yPos = 0;

        // Add blue header background
        doc.setFillColor(25, 85, 155); // Dark Accurix blue
        doc.rect(0, 0, pageWidth, 50, 'F');

        // Title and Company Name in header
        doc.setTextColor(255, 255, 255); // White text
        doc.setFontSize(24);
        doc.setFont(undefined, 'bold');
        doc.text('FINANCIAL REPORT', pageWidth / 2, 20, { align: 'center' });

        doc.setFontSize(14);
        doc.setFont(undefined, 'normal');
        doc.text(reportData.company_name || 'Company', pageWidth / 2, 35, { align: 'center' });

        // Reset text color to black for body
        doc.setTextColor(0, 0, 0);
        yPos = 60;

        // Report Period and Generated Date
        doc.setFontSize(10);
        doc.setFont(undefined, 'normal');
        doc.text(`Report Period: ${startDate} to ${endDate}`, margin, yPos);
        yPos += 7;
        doc.text(`Generated: ${new Date().toLocaleDateString()} ${new Date().toLocaleTimeString()}`, margin, yPos);
        yPos += 15;

        // Financial Summary Section Header
        doc.setFontSize(12);
        doc.setFont(undefined, 'bold');
        doc.text('FINANCIAL SUMMARY', margin, yPos);
        yPos += 10;

        // Summary data with better formatting
        const summaryData = [
          ['Income:', `NGN ${reportData.income?.toLocaleString() || '0'}`],
          ['Expenses:', `NGN ${reportData.expenses?.toLocaleString() || '0'}`],
          ['Profit:', `NGN ${reportData.profit_loss?.toLocaleString() || '0'}`],
          ['Total Transactions:', `${reportData.transaction_count || 0}`]
        ];

        doc.setFontSize(11);
        doc.setFont(undefined, 'normal');
        summaryData.forEach((row, idx) => {
          doc.text(row[0], margin, yPos);
          doc.text(row[1], pageWidth - margin - 40, yPos);
          yPos += 8;
        });

        yPos += 5;

        // Transaction Details Section
        if (reportData.transactions && reportData.transactions.length > 0) {
          // Check if we need a new page
          if (yPos > pageHeight - 80) {
            doc.addPage();
            yPos = margin;
          }

          doc.setFont(undefined, 'bold');
          doc.setFontSize(12);
          doc.setTextColor(0, 0, 0);
          doc.text('TRANSACTION DETAILS', margin, yPos);
          yPos += 12;

          // Create table manually with proper formatting
          const pageWidth_available = pageWidth - 2 * margin;
          const col1 = margin;                    // Date
          const col2 = col1 + 25;                 // Description
          const col3 = col2 + 50;                 // Category
          const col4 = col3 + 30;                 // Type
          const col5 = col4 + 25;                 // Amount
          const rowHeight = 9;
          const headerHeight = 10;
          const cellPadding = 2;

          // Draw header background
          doc.setFillColor(25, 85, 155);
          doc.rect(margin, yPos - 8, pageWidth_available, headerHeight, 'F');

          // Draw header text
          doc.setFont(undefined, 'bold');
          doc.setFontSize(11);
          doc.setTextColor(255, 255, 255);
          doc.text('Date', col1 + cellPadding, yPos + 1);
          doc.text('Description', col2 + cellPadding, yPos + 1);
          doc.text('Category', col3 + cellPadding, yPos + 1);
          doc.text('Type', col4 + cellPadding, yPos + 1);
          doc.text('Amount', col5 + cellPadding, yPos + 1);

          // Draw vertical lines for header
          doc.setDrawColor(255, 255, 255);
          doc.setLineWidth(0.5);
          doc.line(col2 - 2, yPos - 8, col2 - 2, yPos + 2);
          doc.line(col3 - 2, yPos - 8, col3 - 2, yPos + 2);
          doc.line(col4 - 2, yPos - 8, col4 - 2, yPos + 2);
          doc.line(col5 - 2, yPos - 8, col5 - 2, yPos + 2);

          yPos += 8;

          // Draw transaction rows
          doc.setFont(undefined, 'normal');
          doc.setFontSize(9);
          doc.setTextColor(0, 0, 0);

          let rowNum = 0;
          reportData.transactions.forEach((t) => {
            // Check if we need a new page
            if (yPos > pageHeight - margin - 10) {
              doc.addPage();
              yPos = margin;
              
              // Redraw header on new page
              doc.setFillColor(25, 85, 155);
              doc.rect(margin, yPos - 8, pageWidth_available, headerHeight, 'F');
              doc.setFont(undefined, 'bold');
              doc.setFontSize(11);
              doc.setTextColor(255, 255, 255);
              doc.text('Date', col1 + cellPadding, yPos + 1);
              doc.text('Description', col2 + cellPadding, yPos + 1);
              doc.text('Category', col3 + cellPadding, yPos + 1);
              doc.text('Type', col4 + cellPadding, yPos + 1);
              doc.text('Amount', col5 + cellPadding, yPos + 1);

              // Draw vertical lines for header on new page
              doc.setDrawColor(255, 255, 255);
              doc.setLineWidth(0.5);
              doc.line(col2 - 2, yPos - 8, col2 - 2, yPos + 2);
              doc.line(col3 - 2, yPos - 8, col3 - 2, yPos + 2);
              doc.line(col4 - 2, yPos - 8, col4 - 2, yPos + 2);
              doc.line(col5 - 2, yPos - 8, col5 - 2, yPos + 2);

              yPos += 8;
              
              doc.setFont(undefined, 'normal');
              doc.setFontSize(9);
              doc.setTextColor(0, 0, 0);
              rowNum = 0;
            }

            // Alternate row colors
            if (rowNum % 2 === 0) {
              doc.setFillColor(245, 245, 245);
              doc.rect(margin, yPos - 6, pageWidth_available, rowHeight, 'F');
            }

            // Draw horizontal border line
            doc.setDrawColor(200, 200, 200);
            doc.setLineWidth(0.3);
            doc.line(margin, yPos + 1, margin + pageWidth_available, yPos + 1);

            // Draw vertical lines between columns
            doc.setDrawColor(220, 220, 220);
            doc.line(col2 - 2, yPos - 6, col2 - 2, yPos + 1);
            doc.line(col3 - 2, yPos - 6, col3 - 2, yPos + 1);
            doc.line(col4 - 2, yPos - 6, col4 - 2, yPos + 1);
            doc.line(col5 - 2, yPos - 6, col5 - 2, yPos + 1);

            // Draw cell data
            const dateStr = new Date(t.date || t.created_at).toLocaleDateString('en-US', { 
              month: 'short', 
              day: 'numeric', 
              year: 'numeric' 
            });
            const description = (t.description || t.name || 'N/A').substring(0, 18);
            const category = (t.category || 'N/A').substring(0, 12);
            const type = t.type === 'income' ? 'INCOME' : 'EXPENSE';
            const amount = `NGN ${Math.abs(t.amount || 0).toLocaleString()}`;

            doc.setTextColor(0, 0, 0);
            doc.setFontSize(9);
            doc.text(dateStr, col1 + cellPadding, yPos);
            doc.text(description, col2 + cellPadding, yPos);
            doc.text(category, col3 + cellPadding, yPos);
            doc.text(type, col4 + cellPadding, yPos);
            doc.text(amount, col5 + cellPadding, yPos);

            yPos += rowHeight + 1;
            rowNum++;
          });

          // Draw final border
          doc.setDrawColor(200, 200, 200);
          doc.setLineWidth(0.3);
          doc.line(margin, yPos, margin + pageWidth_available, yPos);
        } else {
          // No transactions
          yPos += 5;
          doc.setFont(undefined, 'normal');
          doc.setFontSize(10);
          doc.setTextColor(100, 100, 100);
          doc.text('No transactions found for the selected period.', margin, yPos);
        }

        // Add footer to all pages
        const pageCount = doc.getNumberOfPages();
        for (let i = 1; i <= pageCount; i++) {
          doc.setPage(i);
          
          // Add blue footer background
          doc.setFillColor(25, 85, 155); // Dark Accurix blue
          doc.rect(0, pageHeight - 10, pageWidth, 10, 'F');
          
          // Add footer text
          doc.setFontSize(9);
          doc.setTextColor(255, 255, 255); // White text
          doc.setFont(undefined, 'bold');
          doc.text(
            `Generated by Accurix`,
            pageWidth / 2,
            pageHeight - 6,
            { align: 'center' }
          );
          
          // Add page number at bottom right
          doc.setFontSize(8);
          doc.setFont(undefined, 'normal');
          doc.text(
            `Page ${i} of ${pageCount}`,
            pageWidth - margin - 5,
            pageHeight - 6,
            { align: 'right' }
          );
        }

        // Save the PDF with meaningful filename
        const filename = `Financial-Report-${startDate}_to_${endDate}.pdf`;
        doc.save(filename);
        showNotification('📄 PDF exported successfully!', 'success');
      } else if (format === 'csv') {
        // CSV Export
        let csv = 'Financial Report\n';
        csv += `Report Period: ${startDate} to ${endDate}\n\n`;
        csv += 'Financial Summary\n';
        csv += `Total Income,NGN ${reportData.income?.toLocaleString() || '0'}\n`;
        csv += `Total Expenses,NGN ${reportData.expenses?.toLocaleString() || '0'}\n`;
        csv += `Net Profit,NGN ${reportData.profit_loss?.toLocaleString() || '0'}\n\n`;

        csv += 'Transaction Details\n';
        csv += 'Date,Description,Category,Type,Amount\n';
        if (reportData.transactions) {
          reportData.transactions.forEach(t => {
            csv += `${new Date(t.date || t.created_at).toLocaleDateString()},"${t.description || t.name || 'N/A'}",${t.category || 'N/A'},${(t.type || 'expense').toUpperCase()},${Math.abs(t.amount || 0)}\n`;
          });
        }

        const blob = new Blob([csv], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', 'financial-report.csv');
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        showNotification('📊 CSV exported successfully!', 'success');
      }
    } catch (error) {
      showNotification('❌ Failed to export report. Please try again.', 'error');
    }
  };

  const transactionList = reportData.transactions || [];
  const transactionTotalPages = Math.ceil(transactionList.length / ITEMS_PER_PAGE);
  const transactionStartIndex = (transactionPage - 1) * ITEMS_PER_PAGE;
  const paginatedTransactions = transactionList.slice(transactionStartIndex, transactionStartIndex + ITEMS_PER_PAGE);

  return (
    <AuthenticatedLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-4 sm:gap-0">
          <div>
            <h1 className={`text-2xl sm:text-3xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>Financial Reports</h1>
            <p className={`text-xs sm:text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'} mt-1`}>Get insights and view data from your financial data</p>
          </div>
          <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
            <Button
              variant="secondary"
              onClick={() => handleExport('pdf')}
              className="flex items-center justify-center gap-2 text-sm sm:text-base"
            >
              <Download className="w-4 h-4" />
              <span className="hidden sm:inline">Export as PDF</span>
              <span className="sm:hidden">PDF</span>
            </Button>
            <Button
              variant="secondary"
              onClick={() => handleExport('csv')}
              className="flex items-center justify-center gap-2 text-sm sm:text-base"
            >
              <Download className="w-4 h-4" />
              <span className="hidden sm:inline">Export as CSV</span>
              <span className="sm:hidden">CSV</span>
            </Button>
          </div>
        </div>

        {/* Filters */}
        <Card className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className={`block text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'} mb-2`}>
                <Calendar className="w-4 h-4 inline mr-2" />
                From Date
              </label>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
              />
            </div>
            <div>
              <label className={`block text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'} mb-2`}>
                <Calendar className="w-4 h-4 inline mr-2" />
                To Date
              </label>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
              />
            </div>
          </div>
        </Card>

        {/* Metrics Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
          <div className={`rounded-lg shadow p-4 sm:p-6 border-l-4 border-green-500 ${darkMode ? 'bg-gray-800' : 'bg-white'} relative`}>
            {loading && <div className="absolute inset-0 bg-black/20 rounded-lg flex items-center justify-center"><div className="h-6 w-6 border-2 border-green-500 border-t-transparent rounded-full animate-spin"></div></div>}
            <div className="flex items-center justify-between gap-2">
              <div className="min-w-0">
                <p className={`text-xs sm:text-sm font-medium ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Total Income</p>
                <p className={`text-lg sm:text-2xl font-bold mt-1 sm:mt-2 break-words ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                  ₦{(reportData?.income || 0)?.toLocaleString() || '0'}
                </p>
              </div>
              <div className={`p-2 sm:p-3 rounded-lg flex-shrink-0 ${darkMode ? 'bg-green-900' : 'bg-green-100'}`}>
                <TrendingUp className={`w-5 h-5 sm:w-6 sm:h-6 ${darkMode ? 'text-green-400' : 'text-green-600'}`} />
              </div>
            </div>
          </div>

          <div className={`rounded-lg shadow p-4 sm:p-6 border-l-4 border-red-500 ${darkMode ? 'bg-gray-800' : 'bg-white'} relative`}>
            {loading && <div className="absolute inset-0 bg-black/20 rounded-lg flex items-center justify-center"><div className="h-6 w-6 border-2 border-red-500 border-t-transparent rounded-full animate-spin"></div></div>}
            <div className="flex items-center justify-between gap-2">
              <div className="min-w-0">
                <p className={`text-xs sm:text-sm font-medium ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Total Expenses</p>
                <p className={`text-lg sm:text-2xl font-bold mt-1 sm:mt-2 break-words ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                  ₦{(reportData?.expenses || 0)?.toLocaleString() || '0'}
                </p>
              </div>
              <div className={`p-2 sm:p-3 rounded-lg flex-shrink-0 ${darkMode ? 'bg-red-900' : 'bg-red-100'}`}>
                <TrendingDown className={`w-5 h-5 sm:w-6 sm:h-6 ${darkMode ? 'text-red-400' : 'text-red-600'}`} />
              </div>
            </div>
          </div>

          <div className={`rounded-lg shadow p-4 sm:p-6 border-l-4 border-blue-500 ${darkMode ? 'bg-gray-800' : 'bg-white'} relative`}>
            {loading && <div className="absolute inset-0 bg-black/20 rounded-lg flex items-center justify-center"><div className="h-6 w-6 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div></div>}
            <div className="flex items-center justify-between gap-2">
              <div className="min-w-0">
                <p className={`text-xs sm:text-sm font-medium ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Profit</p>
                <p className={`text-lg sm:text-2xl font-bold mt-1 sm:mt-2 break-words ${(reportData?.profit_loss || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  ₦{(reportData?.profit_loss || 0)?.toLocaleString() || '0'}
                </p>
              </div>
              <div className={`p-2 sm:p-3 rounded-lg flex-shrink-0 ${darkMode ? 'bg-blue-900' : 'bg-blue-100'}`}>
                <DollarSign className={`w-5 h-5 sm:w-6 sm:h-6 ${darkMode ? 'text-blue-400' : 'text-blue-600'}`} />
              </div>
            </div>
          </div>

          <div className={`rounded-lg shadow p-4 sm:p-6 border-l-4 border-purple-500 ${darkMode ? 'bg-gray-800' : 'bg-white'} relative`}>
            {loading && <div className="absolute inset-0 bg-black/20 rounded-lg flex items-center justify-center"><div className="h-6 w-6 border-2 border-purple-500 border-t-transparent rounded-full animate-spin"></div></div>}
            <div className="flex items-center justify-between gap-2">
              <div className="min-w-0">
                <p className={`text-xs sm:text-sm font-medium ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Total Transactions</p>
                <p className={`text-lg sm:text-2xl font-bold mt-1 sm:mt-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                  {reportData?.transaction_count || 0}
                </p>
              </div>
              <div className={`p-2 sm:p-3 rounded-lg flex-shrink-0 ${darkMode ? 'bg-purple-900' : 'bg-purple-100'}`}>
                <CheckCircle className={`w-5 h-5 sm:w-6 sm:h-6 ${darkMode ? 'text-purple-400' : 'text-purple-600'}`} />
              </div>
            </div>
          </div>
        </div>

        {/* Transaction Details */}
        <Card className={darkMode ? 'bg-gray-800' : 'bg-white'}>
          <div className="space-y-3 sm:space-y-4">
            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2 sm:gap-0">
              <h2 className={`text-base sm:text-lg font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>Transaction Details</h2>
              <span className={`text-xs sm:text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Showing {paginatedTransactions.length} of {transactionList.length} transactions</span>
            </div>
              {paginatedTransactions.length > 0 ? (
                <div className="overflow-x-auto -mx-4 sm:mx-0">
                  <div className="inline-block min-w-full px-4 sm:px-0">
                  <table className={`w-full text-xs sm:text-sm ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    <thead className={`border-b ${darkMode ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-200'}`}>
                      <tr>
                        <th className={`px-2 sm:px-4 py-2 sm:py-3 text-left font-medium whitespace-nowrap text-xs sm:text-sm ${darkMode ? 'text-gray-300' : 'text-gray-900'}`}>Date</th>
                        <th className={`px-2 sm:px-4 py-2 sm:py-3 text-left font-medium whitespace-nowrap text-xs sm:text-sm ${darkMode ? 'text-gray-300' : 'text-gray-900'}`}>Description</th>
                        <th className={`px-2 sm:px-4 py-2 sm:py-3 text-left font-medium whitespace-nowrap text-xs sm:text-sm ${darkMode ? 'text-gray-300' : 'text-gray-900'}`}>Category</th>
                        <th className={`hidden sm:table-cell px-2 sm:px-4 py-2 sm:py-3 text-left font-medium whitespace-nowrap text-xs sm:text-sm ${darkMode ? 'text-gray-300' : 'text-gray-900'}`}>Type</th>
                        <th className={`px-2 sm:px-4 py-2 sm:py-3 text-right font-medium whitespace-nowrap text-xs sm:text-sm ${darkMode ? 'text-gray-300' : 'text-gray-900'}`}>Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {paginatedTransactions.map((transaction, idx) => (
                        <tr key={idx} className={`border-b transition ${darkMode ? 'border-gray-700 hover:bg-gray-700' : 'border-gray-200 hover:bg-gray-50'}`}>
                          <td className="px-2 sm:px-4 py-2 sm:py-3 whitespace-nowrap">
                            <span className={`text-xs sm:text-sm ${darkMode ? 'text-gray-300' : 'text-gray-900'}`}>{new Date(transaction.date || transaction.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</span>
                          </td>
                          <td className="px-2 sm:px-4 py-2 sm:py-3 text-xs sm:text-sm truncate max-w-xs sm:max-w-none">
                            <span className={darkMode ? 'text-gray-300' : 'text-gray-700'}>{transaction.description || transaction.name || 'N/A'}</span>
                          </td>
                          <td className="px-2 sm:px-4 py-2 sm:py-3 whitespace-nowrap">
                            <span className={`px-1.5 sm:px-2.5 py-0.5 sm:py-1 rounded text-xs sm:text-xs font-semibold whitespace-nowrap ${darkMode ? 'bg-blue-900 text-blue-200' : 'bg-blue-100 text-blue-800'}`}>
                              {transaction.category || 'Uncategorized'}
                            </span>
                          </td>
                          <td className="hidden sm:table-cell px-2 sm:px-4 py-2 sm:py-3 whitespace-nowrap">
                            <span className={`text-xs sm:text-sm font-semibold ${
                              transaction.type === 'income'
                                ? 'text-green-500'
                                : 'text-red-500'
                            }`}>
                              {(transaction.type === 'income') ? '✓ Income' : '✗ Expense'}
                            </span>
                          </td>
                          <td className="px-2 sm:px-4 py-2 sm:py-3 text-right whitespace-nowrap">
                            <span className={`text-xs sm:text-sm font-bold ${transaction.type === 'income' ? 'text-green-500' : 'text-red-500'}`}>
                              <span className="hidden sm:inline">{(transaction.type === 'income') ? '+' : '-'}</span>₦{Math.abs(transaction.amount || 0).toLocaleString('en-US', { maximumFractionDigits: 0 })}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
              ) : (
                <div className={`flex items-center justify-center h-32 sm:h-40 rounded ${darkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
                  <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>No transactions found</p>
                </div>
              )}
              {transactionTotalPages > 1 && (
                <Pagination 
                  currentPage={transactionPage} 
                  totalPages={transactionTotalPages} 
                  onPageChange={setTransactionPage}
                />
              )}
            </div>
          </Card>


        {/* Toast Notification */}
        {notification && (
          <div className={`fixed bottom-4 right-4 sm:bottom-6 sm:right-6 p-4 sm:p-5 rounded-lg shadow-xl flex items-center gap-3 animate-bounce max-w-sm ${
            notification.type === 'success' 
              ? 'bg-green-500 text-white' 
              : 'bg-red-500 text-white'
          }`}>
            {notification.type === 'success' ? (
              <CheckCircle className="w-5 h-5 flex-shrink-0" />
            ) : (
              <AlertCircle className="w-5 h-5 flex-shrink-0" />
            )}
            <span className="flex-1">{notification.message}</span>
            <button 
              onClick={() => setNotification(null)}
              className="flex-shrink-0 ml-2 hover:opacity-80"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        )}
      </div>
    </AuthenticatedLayout>
  );
};
